import React, { useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import { useThemeContext } from '../../../global/ThemeContext/ThemeContext';
import { AutomationRule } from '../types/rule';
import { formatDate, formatStatus } from '../utils/automationUtils';

interface AutomationCardProps {
  automation: AutomationRule;
  onDelete?: (uuid: string) => void;
  onEdit?: (uuid: string) => void;
}

const AutomationCard: React.FC<AutomationCardProps> = ({ automation, onDelete, onEdit }) => {
  const theme = useThemeContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit?.(automation.uuid);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete?.(automation.uuid);
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        width: '100%',
        padding: '0.75rem 1rem',
        minHeight: '3.5rem',
        borderRadius: '0.5rem',
        background: theme.background.secondary,
        color: theme.text.secondary,
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Tooltip title={automation.name.length > 50 ? automation.name : ''} arrow placement="left">
        <Typography
          variant="body1"
          sx={{
            flex: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: theme.text.primary,
            fontWeight: 400,
          }}
        >
          {automation.name}
        </Typography>
      </Tooltip>
      <Typography
        variant="body1"
        sx={{
          flex: 0.2,
          color: automation.is_active ? theme.success : theme.error,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: automation.is_active ? theme.success : theme.error,
            marginRight: 8,
          }}
        />
        {formatStatus(automation.is_active)}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          flex: 0.2,
          textAlign: 'center',
          fontWeight: 400,
        }}
      >
        {formatDate(automation.created_at)}
      </Typography>
      <Box sx={{ display: 'flex', flex: 0.1, justifyContent: 'center' }}>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreHorizIcon sx={{ color: theme.primary }} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              background: theme.background.primary,
              color: theme.text.primary,
              borderRadius: '8px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              padding: '2px',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.85rem',
              padding: '2px 8px',
              '&:hover': {
                backgroundColor: theme.primary,
                color: theme.background.primary,
                borderRadius: '4px',
              },
            },
          }}
        >
          <MenuItem onClick={handleEdit}>Editar</MenuItem>
          <MenuItem onClick={handleDelete}>Excluir</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AutomationCard;
